import { useEffect } from 'react';
import { isConfigurableFlowToolId } from '../../../constants/configurableFlowToolIds';

import { readAiDisabledFromToolArgs } from '../../../utils/purchaseIntentionAiDisabled';
import { readIncludeImagesFromToolArgs } from '../../../utils/retrieveProductsIncludeImages';

/**
 * When opening the modal to edit a flow step, hydrates dropdowns from initialArgs once.
 * retailer_api_request hydration lives in useRetailerApiRequestTool.
 */
const useInitialToolArgsSync = ({
  initialTool,
  initialArgs,
  teamOptions,
  funnelOptions,
  setSelectedTeam,
  setSelectedFileIds,
  setSelectedWhatsappFlowId,
  setSelectedFunnel,
  setSelectedStep,
  setPurchaseIntentionAiDisabled,
  setRetrieveProductsIncludeImages,
  setSelectedCrmFieldIds,
  appliedInitialArgsRef
}) => {
  useEffect(() => {
    if (!initialTool) return;
    if (initialTool.id === 'retailer_api_request') return;
    if (!isConfigurableFlowToolId(initialTool.id)) return;
    if (appliedInitialArgsRef.current) return;

    // retrieve_products: empty args means include images (default ON)
    if (initialTool.id === 'retrieve_products') {
      setRetrieveProductsIncludeImages(readIncludeImagesFromToolArgs(initialArgs));
      appliedInitialArgsRef.current = true;
      return;
    }

    if (!initialArgs || !Array.isArray(initialArgs) || initialArgs.length === 0) return;

    if (['purchase_intention', 'create_order', 'get_payment_link'].includes(initialTool.id)) {
      setPurchaseIntentionAiDisabled(readAiDisabledFromToolArgs(initialArgs));
      appliedInitialArgsRef.current = true;
      return;
    }

    const args0 = initialArgs[0] || {};

    if (initialTool.id === 'assignment') {
      const rawAssignableId = args0.assignable_id ?? args0.team_id ?? args0.team ?? null;
      if (!rawAssignableId) return;

      const team = teamOptions.find((opt) => String(opt.id) === String(rawAssignableId));
      if (!team) return;

      setSelectedTeam(team.id);
      appliedInitialArgsRef.current = true;
      return;
    }

    if (initialTool.id === 'send_file') {
      const ids = (initialArgs || [])
        .map((a) => a?.media_id ?? a?.file_id ?? a?.id ?? null)
        .filter(Boolean)
        .map((x) => String(x))
        .slice(0, 5);
      setSelectedFileIds(ids);
      appliedInitialArgsRef.current = true;
      return;
    }

    if (initialTool.id === 'trigger_whatsapp_flow') {
      const flowId = args0.whatsapp_flow_id ?? args0.flow_id ?? args0.meta_flow_id ?? null;
      if (!flowId) return;

      setSelectedWhatsappFlowId(String(flowId));
      appliedInitialArgsRef.current = true;
      return;
    }

    if (initialTool.id === 'get_customer_crm_data') {
      const fields = args0.fields;
      if (!Array.isArray(fields) || fields.length === 0) return;

      setSelectedCrmFieldIds(fields.map((field) => String(field)).filter(Boolean).slice(0, 100));
      appliedInitialArgsRef.current = true;
      return;
    }

    const rawFunnelId = args0.funnel_id ?? null;
    const rawStepId = args0.funnel_step_id ?? null;
    const rawStepWebId = args0.funnel_step_web_id ?? null;
    if (!rawFunnelId) return;

    const funnel = funnelOptions.find(
      (opt) => String(opt.id) === String(rawFunnelId) || String(opt.web_id) === String(rawFunnelId)
    );

    if (!funnel) return;

    setSelectedFunnel(funnel.id);

    const desiredStepIdentifier = rawStepId || rawStepWebId;
    if (!desiredStepIdentifier) {
      appliedInitialArgsRef.current = true;
      return;
    }

    const step = (funnel.funnel_steps || []).find(
      (s) => String(s.id) === String(desiredStepIdentifier) || String(s.web_id) === String(desiredStepIdentifier)
    );
    if (!step) return;

    setSelectedStep(step.id);
    appliedInitialArgsRef.current = true;
  }, [
    appliedInitialArgsRef,
    funnelOptions,
    teamOptions,
    initialArgs,
    initialTool,
    setSelectedTeam,
    setSelectedFileIds,
    setSelectedWhatsappFlowId,
    setSelectedFunnel,
    setSelectedStep,
    setPurchaseIntentionAiDisabled,
    setRetrieveProductsIncludeImages,
    setSelectedCrmFieldIds
  ]);
};

export default useInitialToolArgsSync;
